import React, { Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { Toaster } from './components/ui/sonner';
import { SiteHome } from './pages/site/Home';
import { NotFound } from './pages/NotFound';
import './index.css';

/* Pages publiques chargées à la demande : la page d'accueil s'ouvre vite, chaque page ne charge que son code */
const SiteAbout = lazy(() => import('./pages/site/About').then(m => ({ default: m.SiteAbout })));
const SiteServices = lazy(() => import('./pages/site/Services').then(m => ({ default: m.SiteServices })));
const SiteParticuliers = lazy(() => import('./pages/site/Offres').then(m => ({ default: m.SiteParticuliers })));
const SiteParticulierOffre = lazy(() => import('./pages/site/Offres').then(m => ({ default: m.SiteParticulierOffre })));
const SiteProfessionnels = lazy(() => import('./pages/site/Offres').then(m => ({ default: m.SiteProfessionnels })));
const SiteProOffre = lazy(() => import('./pages/site/Offres').then(m => ({ default: m.SiteProOffre })));
const SimulateurSolaire = lazy(() => import('./pages/site/SimulateurSolaire'));
const Aides = lazy(() => import('./pages/site/Aides'));
const Qualifications = lazy(() => import('./pages/site/Qualifications'));
const Parrainage = lazy(() => import('./pages/site/Parrainage'));
const VisiteTechnique = lazy(() => import('./pages/site/VisiteTechnique'));
const ServiceDepartement = lazy(() => import('./pages/site/Departements').then(m => ({ default: m.ServiceDepartement })));
const ServiceIndex = lazy(() => import('./pages/site/Departements').then(m => ({ default: m.ServiceIndex })));
const SiteDevis = lazy(() => import('./pages/site/Devis').then(m => ({ default: m.SiteDevis })));
const SiteContact = lazy(() => import('./pages/site/Contact').then(m => ({ default: m.SiteContact })));
const SiteActualites = lazy(() => import('./pages/site/Actualites').then(m => ({ default: m.SiteActualites })));
const SiteGuides = lazy(() => import('./pages/site/Guides').then(m => ({ default: m.SiteGuides })));
const SiteGuide = lazy(() => import('./pages/site/Guides').then(m => ({ default: m.SiteGuide })));
const SiteRealisations = lazy(() => import('./pages/site/Realisations').then(m => ({ default: m.SiteRealisations })));
const SiteRecrutement = lazy(() => import('./pages/site/Recrutement').then(m => ({ default: m.SiteRecrutement })));
const PageTypeChantier = lazy(() => import('./pages/site/TypesChantier').then(m => ({ default: m.PageTypeChantier })));
const PrestationPV = lazy(() => import('./pages/site/PrestationsPV'));
const SitePartenaire = lazy(() => import('./pages/site/Partenaire').then(m => ({ default: m.SitePartenaire })));
const SiteDocuments = lazy(() => import('./pages/site/Documents').then(m => ({ default: m.SiteDocuments })));
const SiteSimulateur = lazy(() => import('./pages/site/Simulateur').then(m => ({ default: m.SiteSimulateur })));
const SiteFaq = lazy(() => import('./pages/site/Faq').then(m => ({ default: m.SiteFaq })));
const SitePourquoi = lazy(() => import('./pages/site/PourquoiNous').then(m => ({ default: m.SitePourquoi })));
const SiteRendezVous = lazy(() => import('./pages/site/RendezVous').then(m => ({ default: m.SiteRendezVous })));
const SiteDisponibilites = lazy(() => import('./pages/site/Disponibilites').then(m => ({ default: m.SiteDisponibilites })));
const SiteActivites = lazy(() => import('./pages/site/Activites').then(m => ({ default: m.SiteActivites })));
const MentionsLegales = lazy(() => import('./pages/site/MentionsLegales').then(m => ({ default: m.MentionsLegales })));
const CGU = lazy(() => import('./pages/site/CGU').then(m => ({ default: m.CGU })));
const PageVille = lazy(() => import('./pages/site/VillesLocales').then(m => ({ default: m.PageVille })));
const SERVICES_DEP_CLES = ["panneaux-solaires", "borne-de-recharge", "pompe-a-chaleur", "climatisation", "nettoyage-panneaux-solaires", "sous-traitance-fibre-ftth", "sous-traitance-irve"];
const VILLES_SLUGS = ["bordeaux", "paris", "lyon", "marseille", "toulouse", "lille", "strasbourg", "nantes", "rennes", "nice", "montpellier", "rouen", "marmande", "agen", "pau", "mont-de-marsan", "perigueux", "montauban", "la-rochelle", "poitiers", "limoges", "angouleme", "bayonne", "tours", "clermont-ferrand", "grenoble", "nimes", "perpignan"];
const Login = lazy(() => import('./pages/Login').then(m => ({ default: m.Login })));
const Register = lazy(() => import('./pages/Register').then(m => ({ default: m.Register })));
const AdminDashboard = lazy(() => import('./pages/admin/AdminDashboard').then(m => ({ default: m.AdminDashboard })));
const TechRegistration = lazy(() => import('./pages/tech/TechRegistration').then(m => ({ default: m.TechRegistration })));
const TechDashboard = lazy(() => import('./pages/tech/TechDashboard').then(m => ({ default: m.TechDashboard })));
const ModeratorDashboard = lazy(() => import('./pages/moderator/ModeratorDashboard').then(m => ({ default: m.ModeratorDashboard })));
const ClientDashboard = lazy(() => import('./pages/client/ClientDashboard').then(m => ({ default: m.ClientDashboard })));
const EtudePhoto = lazy(() => import('./pages/site/EtudePhoto'));
const SuiviChantier = lazy(() => import('./pages/site/SuiviChantier'));
const SignerDevis = lazy(() => import('./pages/site/SignerDevis').then(m => ({ default: m.SignerDevis })));
const SiteDemo = lazy(() => import('./pages/site/Demo').then(m => ({ default: m.SiteDemo })));
const SiteFormation = lazy(() => import('./pages/site/FormationSite').then(m => ({ default: m.SiteFormation })));
const SiteFormationEspace = lazy(() => import('./pages/site/FormationSite').then(m => ({ default: m.SiteFormationEspace })));
const SiteAttestation = lazy(() => import('./pages/site/FormationSite').then(m => ({ default: m.SiteAttestation })));
const SousTraitancePV = lazy(() => import('./pages/site/SousTraitancePV'));
const References = lazy(() => import('./pages/site/References'));
const ReserverEquipe = lazy(() => import('./pages/site/ReserverEquipe'));
const GrandsComptes = lazy(() => import('./pages/site/GrandsComptes'));
const Appli = lazy(() => import('./pages/site/Appli'));
const Controle = lazy(() => import('./pages/site/Controle'));
const PanneauxSolairesPV = lazy(() => import('./pages/site/PanneauxSolairesPV'));
const PanneauxNA = lazy(() => import('./pages/site/NouvelleAquitaine').then(m => ({ default: m.PanneauxNouvelleAquitaine })));
const PanneauxVilleNA = lazy(() => import('./pages/site/NouvelleAquitaine').then(m => ({ default: m.PanneauxVilleNA })));
const DossierFournisseur = lazy(() => import('./pages/site/DossierFournisseur'));
const DossierTelecharger = lazy(() => import('./pages/site/DossierTelecharger'));
const SuiviClient = lazy(() => import('./pages/site/SuiviClient'));
const FibreBouche = lazy(() => import('./pages/site/FibreFTTH').then(m => ({ default: m.FibreBouche })));
const FibreRegion = lazy(() => import('./pages/site/FibreFTTH').then(m => ({ default: m.FibreRegion })));
const FibreVille = lazy(() => import('./pages/site/FibreFTTH').then(m => ({ default: m.FibreVille })));
const ConseilsIndex = lazy(() => import('./pages/site/Conseils').then(m => ({ default: m.ConseilsIndex })));
const ConseilArticle = lazy(() => import('./pages/site/Conseils').then(m => ({ default: m.ConseilArticle })));
const EnglishHome = lazy(() => import('./pages/site/EnglishHome'));
const SiteEnglish = lazy(() => import('./pages/site/English').then(m => ({ default: m.SiteEnglish })));
const SiteJeu = lazy(() => import('./pages/site/Jeu').then(m => ({ default: m.SiteJeu })));
const SiteJeuEn = lazy(() => import('./pages/site/Jeu').then(m => ({ default: m.SiteJeuEn })));
const SiteJeuExpert = lazy(() => import('./pages/site/JeuExpert').then(m => ({ default: m.SiteJeuExpert })));
const ForgotPassword = lazy(() => import('./pages/ForgotPassword').then(m => ({ default: m.ForgotPassword })));

const PageLoader = () => (
  <div style={{ minHeight: '60vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
    <div style={{ width: 44, height: 44, border: '4px solid #002FA7', borderTopColor: 'transparent', borderRadius: '50%', animation: 'evspin .8s linear infinite' }} />
    <style>{'@keyframes evspin{to{transform:rotate(360deg)}}'}</style>
  </div>
);

const ProtectedRoute = ({ children, allowedRoles }) => {
  const { user, loading } = useAuth();
  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-[#FAFAFA]">
      <div className="text-center">
        <div className="inline-block w-16 h-16 border-4 border-[#002FA7] border-t-transparent rounded-full animate-spin"></div>
        <p className="mt-4 text-[#52525B]">Chargement...</p>
      </div>
    </div>
  );
  if (!user) return <Navigate to="/login" replace />;
  if (allowedRoles && !allowedRoles.includes(user.role)) {
    const roleRoutes = { admin: '/admin', moderator: '/moderator', tech: '/tech', client: '/client' };
    return <Navigate to={roleRoutes[user.role] || '/login'} replace />;
  }
  return children;
};

function AppRoutes() {
  return (
    <Suspense fallback={<PageLoader />}>
    <Routes>
      {/* Site vitrine public */}
      <Route path="/" element={<SiteHome />} />
      <Route path="/qui-sommes-nous" element={<SiteAbout />} />
      <Route path="/nos-activites" element={<SiteServices />} />
      <Route path="/nos-activites/:serviceId" element={<SiteServices />} />
      <Route path="/nos-activites/:serviceId/:region" element={<SiteServices />} />
      <Route path="/simulateur-solaire" element={<SimulateurSolaire />} />
      <Route path="/aides-2026" element={<Aides />} />
      <Route path="/sous-traitance-photovoltaique" element={<SousTraitancePV />} />
      <Route path="/references-photovoltaique" element={<References />} />
      <Route path="/reserver-equipe" element={<ReserverEquipe />} />
      <Route path="/grands-comptes" element={<GrandsComptes />} />
      <Route path="/appli" element={<Appli />} />
      <Route path="/controle" element={<Controle />} />
      <Route path="/panneaux-solaires-photovoltaiques" element={<PanneauxSolairesPV />} />
      <Route path="/panneaux-solaires-nouvelle-aquitaine" element={<PanneauxNA />} />
      <Route path="/panneaux-solaires-nouvelle-aquitaine/:ville" element={<PanneauxVilleNA />} />
      <Route path="/dossier-fournisseur" element={<DossierFournisseur />} />
      <Route path="/dossier-fournisseur/telecharger" element={<DossierTelecharger />} />
      <Route path="/suivi-client" element={<SuiviClient />} />
      <Route path="/particuliers/fibre-fourreau-bouche" element={<FibreBouche />} />
      <Route path="/fibre-optique-ftth-gironde" element={<FibreRegion />} />
      <Route path="/fibre-optique-ftth/:ville" element={<FibreVille />} />
      <Route path="/sous-traitance-photovoltaique/:region" element={<SousTraitancePV />} />
      <Route path="/conseils" element={<ConseilsIndex />} />
      <Route path="/conseils/:slug" element={<ConseilArticle />} />
      <Route path="/qualifications" element={<Qualifications />} />
      <Route path="/parrainage" element={<Parrainage />} />
      <Route path="/etude-photo" element={<EtudePhoto />} />
      <Route path="/visite-technique" element={<VisiteTechnique />} />
      <Route path="/suivi/:jeton" element={<SuiviChantier />} />
      {SERVICES_DEP_CLES.map(k => <Route key={k} path={`/${k}`} element={<ServiceIndex service={k} />} />)}
      {SERVICES_DEP_CLES.map(k => <Route key={k + '-dep'} path={`/${k}/:dep`} element={<ServiceDepartement service={k} />} />)}
      <Route path="/particuliers" element={<SiteParticuliers />} />
      <Route path="/particuliers/:service" element={<SiteParticulierOffre />} />
      <Route path="/particuliers/:service/:region" element={<SiteParticulierOffre />} />
      <Route path="/professionnels" element={<SiteProfessionnels />} />
      <Route path="/professionnels/:service" element={<SiteProOffre />} />
      <Route path="/professionnels/:service/:region" element={<SiteProOffre />} />
      <Route path="/devis" element={<SiteDevis />} />
      <Route path="/contact" element={<SiteContact />} />
      <Route path="/actualites" element={<SiteActualites />} />
      <Route path="/nos-realisations" element={<SiteRealisations />} />
      <Route path="/recrutement" element={<SiteRecrutement />} />
      <Route path="/signer-devis/:jeton" element={<SignerDevis />} />
      <Route path="/demo" element={<SiteDemo />} />
      <Route path="/formation" element={<SiteFormation />} />
      <Route path="/formation/espace" element={<SiteFormationEspace />} />
      <Route path="/attestation/:numero" element={<SiteAttestation />} />
      <Route path="/prestations-photovoltaiques/:id" element={<PrestationPV />} />
      <Route path="/sous-traitance/:typeId" element={<PageTypeChantier />} />
      <Route path="/sous-traitance/:typeId/:region" element={<PageTypeChantier />} />
      <Route path="/devenir-partenaire" element={<SitePartenaire />} />
      <Route path="/devenir-sous-traitant-fibre-ftth" element={<SitePartenaire ftth />} />
      <Route path="/activites" element={<SiteActivites />} />
      <Route path="/documents" element={<SiteDocuments />} />
      <Route path="/en" element={<SiteEnglish />} />
      <Route path="/en/home" element={<EnglishHome />} />
      <Route path="/simulateur" element={<SiteSimulateur />} />
      <Route path="/jeu" element={<SiteJeu />} />
      <Route path="/en/game" element={<SiteJeuEn />} />
      <Route path="/jeu-expert" element={<SiteJeuExpert />} />
      <Route path="/guides" element={<SiteGuides />} />
      <Route path="/guides/:slug" element={<SiteGuide />} />
      <Route path="/faq" element={<SiteFaq />} />
      <Route path="/pourquoi-evolink" element={<SitePourquoi />} />
      <Route path="/rendez-vous" element={<SiteRendezVous />} />
      <Route path="/disponibilites" element={<SiteDisponibilites />} />

      {/* Auth */}
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/mentions-legales" element={<MentionsLegales />} />
      <Route path="/cgu" element={<CGU />} />
      {VILLES_SLUGS.map(v => <Route key={v} path={`/installation-panneaux-solaires-${v}`} element={<PageVille slug={v} />} />)}

      {/* Espaces proteges */}
      <Route path="/admin" element={<ProtectedRoute allowedRoles={['admin']}><AdminDashboard /></ProtectedRoute>} />
      <Route path="/moderator" element={<ProtectedRoute allowedRoles={['moderator']}><ModeratorDashboard /></ProtectedRoute>} />
      <Route path="/tech" element={<ProtectedRoute allowedRoles={['tech']}><TechDashboard /></ProtectedRoute>} />
      <Route path="/tech/registration" element={<ProtectedRoute allowedRoles={['tech']}><TechRegistration /></ProtectedRoute>} />
      <Route path="/client" element={<ProtectedRoute allowedRoles={['client']}><ClientDashboard /></ProtectedRoute>} />

      <Route path="*" element={<NotFound />} />
    </Routes>
    </Suspense>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRoutes />
        <Toaster position="top-right" richColors />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
